/**
 * 阿里云 CDN 图片优化 Loader
 *
 * 基于阿里云 OSS 图片处理参数文档
 * https://help.aliyun.com/zh/cdn/user-guide/resize-images
 *
 * 支持的优化功能：
 * - 自动调整图片尺寸（resize）
 * - 质量压缩（quality）
 * - 格式转换（format - WebP/JPEG/PNG）
 * - 图片锐化（sharpen）
 * - 自动旋转（auto-orient）
 * - 渐进式显示（interlace）
 * - 图片裁剪（crop）
 * - 圆角处理（rounded-corners）
 *
 * @see https://help.aliyun.com/zh/cdn/user-guide/resize-images
 */

import {
  shouldOptimizeImage,
  IMAGE_OPTIMIZATION_CONFIG,
  SKIP_OPTIMIZATION_FORMATS,
} from "./imageLoader.config.js";

/**
 * 判断是否为本地静态资源
 * @param {string} src
 * @returns {boolean}
 */
function isLocalAsset(src) {
  if (!src) {
    return false;
  }
  // 绝对路径但未包含协议，且以单斜杠开头，例如 /assets/foo.png
  if (src.startsWith("/") && !src.startsWith("//")) {
    return true;
  }
  // ./ 或 ../ 形式的相对路径
  return src.startsWith("./") || src.startsWith("../");
}

/**
 * 构建用于本地资源的 URL，确保包含 width/quality 参数
 * 这样可满足 Next.js 对自定义 loader 的校验需求
 * @param {string} src
 * @param {number} width
 * @param {number} quality
 * @returns {string}
 */
function appendWidthQualityParams(src, width, quality) {
  const params = [];

  if (width) {
    params.push(`w=${width}`);
  }

  if (quality) {
    params.push(`q=${quality}`);
  }

  if (params.length === 0) {
    return src;
  }

  const separator = src.includes("?") ? "&" : "?";
  return `${src}${separator}${params.join("&")}`;
}

function buildLocalAssetUrl(src, width, quality) {
  return appendWidthQualityParams(src, width, quality);
}

/**
 * 检查是否为需要跳过优化的格式
 *
 * @param {string} src - 图片源 URL
 * @returns {boolean}
 */
function shouldSkipOptimization(src) {
  return SKIP_OPTIMIZATION_FORMATS.some((format) =>
    src.toLowerCase().endsWith(format),
  );
}

/**
 * 构建图片缩放参数
 *
 * 阿里云 CDN 支持的缩放参数：
 * - w: 指定目标宽度
 * - h: 指定目标高度
 * - p: 指定缩放百分比
 *
 * 注意：CDN 图片缩放只支持缩小，不支持放大
 *
 * @param {number} width - 目标宽度
 * @param {number} height - 目标高度
 * @returns {string}
 */
function buildResizeParams(width, height = null) {
  const params = ["resize"];

  if (width) {
    params.push(`w_${width}`);
  }

  if (height) {
    params.push(`h_${height}`);
  }

  return params.join(",");
}

/**
 * 构建质量压缩参数
 *
 * 阿里云 CDN 质量参数：
 * - quality,q_<value>: 取值范围 1-100，数值越大质量越高
 *
 * @param {number} quality - 质量值 (1-100)
 * @returns {string}
 */
function buildQualityParams(quality) {
  const qualityValue = quality || IMAGE_OPTIMIZATION_CONFIG.defaultQuality;

  // 确保质量值在有效范围内
  const clampedQuality = Math.max(1, Math.min(100, qualityValue));

  return `quality,q_${clampedQuality}`;
}

/**
 * 构建格式转换参数
 *
 * 阿里云 CDN 支持的格式：
 * - webp: WebP 格式（推荐）
 * - jpg: JPEG 格式
 * - png: PNG 格式
 *
 * @param {string} format - 目标格式
 * @returns {string}
 */
function buildFormatParams(format = "webp") {
  // 格式验证（CDN 主要支持这三种）
  const validFormats = ["webp", "jpg", "png"];
  const targetFormat = validFormats.includes(format.toLowerCase())
    ? format.toLowerCase()
    : "webp";

  return `format,${targetFormat}`;
}

/**
 * 自定义图片 Loader（Next.js Image 组件使用）
 *
 * @param {Object} params - Next.js 传入的参数
 * @param {string} params.src - 图片源 URL
 * @param {number} params.width - 目标宽度
 * @param {number} params.quality - 图片质量 (1-100)
 * @returns {string} 优化后的图片 URL
 */
export default function aliyunImageLoader({ src, width, quality }) {
  // 本地静态资源直接返回，避免触发远程优化逻辑
  // 同时带上 width/quality 以满足 Next.js 的校验
  if (isLocalAsset(src)) {
    return buildLocalAssetUrl(src, width, quality);
  }

  // SVG、GIF 等特殊格式不需要优化，直接返回原始 URL
  if (shouldSkipOptimization(src)) {
    return appendWidthQualityParams(src, width, quality);
  }

  // 检查是否需要优化此图片
  if (!shouldOptimizeImage(src)) {
    return appendWidthQualityParams(src, width, quality);
  }

  // 如果 URL 已经包含图片处理参数，避免重复处理
  if (src.includes("image_process=")) {
    return src;
  }

  // 构建图片处理参数数组
  const processParams = [];

  // 1. 图片缩放 - 只支持缩小，不支持放大
  if (width) {
    processParams.push(buildResizeParams(width));
  }

  // 2. 质量压缩 - 调整图片质量
  processParams.push(buildQualityParams(quality));

  // 3. 格式转换 - 转换为 WebP 格式
  // WebP 在保持相同视觉质量下，文件大小更小
  if (IMAGE_OPTIMIZATION_CONFIG.enableWebP) {
    processParams.push(buildFormatParams("webp"));
  }

  // 4. 图片锐化 - 增强图片清晰度
  if (IMAGE_OPTIMIZATION_CONFIG.enableSharpen) {
    processParams.push(`sharpen,${IMAGE_OPTIMIZATION_CONFIG.sharpenValue}`);
  }

  // 组合所有处理参数 - 使用斜杠分隔不同操作
  const combinedParams = processParams.join("/");

  // 构建完整的 URL
  // 格式：http://example.com/image.jpg?image_process=resize,w_200/quality,q_75/format,webp
  const separator = src.includes("?") ? "&" : "?";
  return `${src}${separator}image_process=${combinedParams}`;
}

/**
 * 高级图片处理函数（可用于特殊场景）
 *
 * @param {string} src - 图片源 URL
 * @param {Object} options - 处理选项
 * @param {number} options.width - 宽度
 * @param {number} options.height - 高度
 * @param {number} options.quality - 质量
 * @param {string} options.format - 格式 (webp/jpg/png)
 * @param {boolean} options.crop - 是否裁剪
 * @param {number} options.rotate - 旋转角度（顺时针）
 * @returns {string}
 */
export function customImageLoader(src, options = {}) {
  // SVG、GIF 等特殊格式不需要处理
  if (shouldSkipOptimization(src)) {
    return src;
  }

  // 检查是否需要优化
  if (!shouldOptimizeImage(src)) {
    return src;
  }

  const {
    width,
    height,
    quality,
    format = "webp",
    crop = false,
    rotate = 0,
  } = options;

  const processParams = [];

  // 如果需要裁剪
  if (crop && width && height) {
    // 裁剪参数：crop,x_起点,y_起点,w_宽度,h_高度
    processParams.push(`crop,x_0,y_0,w_${width},h_${height}`);
  }

  // 调整尺寸
  if (width || height) {
    processParams.push(buildResizeParams(width, height));
  }

  // 质量压缩
  processParams.push(buildQualityParams(quality));

  // 格式转换
  processParams.push(buildFormatParams(format));

  // 图片旋转
  if (rotate > 0) {
    processParams.push(`rotate,${rotate}`);
  }

  // 锐化
  if (IMAGE_OPTIMIZATION_CONFIG.enableSharpen) {
    processParams.push(`sharpen,${IMAGE_OPTIMIZATION_CONFIG.sharpenValue}`);
  }

  // 使用斜杠分隔不同操作
  const combinedParams = processParams.join("/");
  const separator = src.includes("?") ? "&" : "?";
  return `${src}${separator}image_process=${combinedParams}`;
}

/**
 * 生成不同尺寸的图片 URL（用于响应式图片）
 *
 * @param {string} src - 图片源 URL
 * @param {number[]} sizes - 尺寸数组，如 [320, 640, 1024, 1920]
 * @param {number} quality - 质量
 * @returns {Object} 包含不同尺寸的 URL 对象
 */
export function generateResponsiveUrls(
  src,
  sizes = [320, 640, 1024, 1920],
  quality = 75,
) {
  // SVG、GIF 等特殊格式不需要生成多个尺寸
  if (shouldSkipOptimization(src)) {
    return { default: src };
  }

  if (!shouldOptimizeImage(src)) {
    return { default: src };
  }

  const urls = {};

  sizes.forEach((size) => {
    urls[`w${size}`] = aliyunImageLoader({ src, width: size, quality });
  });

  urls.default = src;

  return urls;
}
